import { useState, useEffect, useCallback, useMemo } from 'react';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { useProducts } from '@/contexts/ProductsContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import SearchableSelect from '@/components/SearchableSelect';
import { trTutar, trPara } from '@/lib/sayi';
import { toast } from 'sonner';
import {
  Download, FlaskConical, Play, RotateCcw, Loader2, Settings, Layers, AlertTriangle, Eye, CheckCircle,
  Link2, RefreshCw, Clock,
} from 'lucide-react';

/**
 * POS → ERP PARTİLİ AKTARIM (kullanıcı, 2026-10-04: "Bir hata olduğunda tüm
 * aktarımı ya da seçili aktarımları geri alabiliyor olmamız lazım").
 *
 * Sıra ekranda da korunur: EŞLEŞTİR → ÇEK → DENEME → UYGULA → (gerekirse)
 * GERİ AL. Deneme hiçbir şey yazmaz ve uygulamayla aynı planlayıcıdan geçer
 * (`pos_aktarim.adisyon_plani`). Eşleştirme BİR KEZ yapılır: POS'tan gelen her
 * hesap (ödeme yöntemi + seçilen banka, nakitte para birimi, bahşiş, paket
 * kredisi, hediye çeki, veresiye müşterisi, üretim noktası) ilk geldiğinde
 * bağlanır; eşleşmesi olmayan kayıt yazılmaz, bekler.
 */

const ODEME_DURUM = {
  hesap: 'Hesaba yazılacak', cari: 'Cariye yazılacak', eslesmesiz: 'EŞLEŞTİRME BEKLİYOR',
  zaten_var: 'Zaten yazılmış', onceden_odendi: 'Önceden ödendi (kayıt yok)', musterisiz: 'Müşterisiz veresiye', para_ustu: 'Para üstü (kasadan çıkış)',
};
const RECETE_DURUM = {
  hizmet: 'Hizmet satışı', ucretli_tuketim: 'Ücretli tüketim karşılığı',
  tur_uyumsuz: 'Kart mamul değil',
  tamam: 'Reçeteli', dogrudan: 'Doğrudan stoktan (olduğu gibi satılan)', kodsuz: 'POS kodu yok', kart_yok: 'Kod ERP\'de yok', kart_pasif: 'Kart pasif',
  recete_yok: 'Reçetesi yok', hediye_ceki: 'Hediye çeki satışı', paket: 'Paket hizmet bedeli',
  paket_sure: 'Süresi dolan paket kredisi',
  fix_menu: 'Fix menü (maliyeti bağlı tüketimden)', kapora: 'Fix menü kaporası (ciro değil)',
};
const STAGING_DURUM = {
  hazir: 'Hazır', completed: 'İşlendi', geri_alindi: 'Geri alındı', failed: 'Bekliyor/hatalı',
  pending: 'Canlı bekliyor', processing: 'İşleniyor',
};
const PARTI_DURUM = {
  calisiyor: 'Çalışıyor', tamamlandi: 'Tamamlandı', hatali: 'Yarıda kaldı', acik: 'Açık (canlı)',
  geri_alindi: 'Geri alındı', kismen_geri_alindi: 'Kısmen geri alındı',
};
const ESLEME_TURU = { odeme: 'Ödeme hesabı', musteri: 'Veresiye müşterisi', depo: 'Üretim noktası → depo' };

const bugun = () => new Date().toISOString().slice(0, 10);
const hata = (e, varsayilan) => e?.response?.data?.detail || varsayilan;

export default function PosAktarim() {
  const { hasPermission } = useAuth();
  const duzenler = hasPermission('pos_integration', 'edit');
  const silebilir = hasPermission('pos_integration', 'delete');

  const [ayar, setAyar] = useState(null);
  const [ozet, setOzet] = useState(null);
  const [partiler, setPartiler] = useState([]);
  const [eslesme, setEslesme] = useState(null);
  const [aralik, setAralik] = useState({ bas: '2026-03-29', bit: bugun() });
  const [mesgul, setMesgul] = useState('');
  const [rapor, setRapor] = useState(null);
  const [onay, setOnay] = useState(null);       // {govde, baslik}
  const [detayId, setDetayId] = useState(null);

  const eslesmeYukle = useCallback(() => api.get('/pos/aktarim/eslestirmeler').then(r => setEslesme(r.data)), []);
  const yukle = useCallback(async () => {
    const [a, o, p] = await Promise.all([
      api.get('/pos/aktarim/ayarlar'), api.get('/pos/aktarim/ozet'), api.get('/pos/aktarim/partiler'),
    ]);
    setAyar(a.data); setOzet(o.data); setPartiler(p.data);
    await eslesmeYukle();
  }, [eslesmeYukle]);

  useEffect(() => { yukle().catch(() => toast.error('Aktarım bilgileri alınamadı')); }, [yukle]);

  // Çalışan parti varken ilerleme izlenir
  const calisanVar = partiler.some(p => p.durum === 'calisiyor');
  useEffect(() => {
    if (!calisanVar) return undefined;
    const t = setInterval(() => {
      api.get('/pos/aktarim/partiler').then(r => {
        setPartiler(r.data);
        if (!r.data.some(p => p.durum === 'calisiyor')) {
          api.get('/pos/aktarim/ozet').then(o => setOzet(o.data));
          toast.success('Aktarım partisi bitti');
        }
      }).catch(() => {});
    }, 2500);
    return () => clearInterval(t);
  }, [calisanVar]);

  const cek = async () => {
    setMesgul('cek');
    try {
      const { data: d } = await api.post('/pos/aktarim/cek', aralik);
      toast.success(`POS'tan çekildi: ${d.adisyon} adisyon, ${d.paket} paket satışı, ${d.tahsilat} tahsilat `
        + `(${d.yeni} yeni, ${d.tazelenen} tazelendi, ${d.islenmis} zaten işlenmiş)`
        + (d.degisen ? ` — ${d.degisen} işlenmiş kayıt POS'ta DEĞİŞMİŞ` : ''));
      await yukle();
    } catch (e) { toast.error(hata(e, 'Çekilemedi')); } finally { setMesgul(''); }
  };

  const deneme = async () => {
    setMesgul('deneme');
    try {
      const r = await api.post('/pos/aktarim/deneme', aralik);
      setRapor({ ...r.data, aralik: { ...aralik } });
    } catch (e) { toast.error(hata(e, 'Deneme raporu alınamadı')); } finally { setMesgul(''); }
  };

  const uygula = async () => {
    const { govde } = onay;
    setOnay(null);
    setMesgul('uygula');
    try {
      const r = await api.post('/pos/aktarim/uygula', govde);
      toast.success(`Parti başlatıldı: ${r.data.kayit} kayıt işlenecek`);
      setRapor(null);
      await yukle();
    } catch (e) { toast.error(hata(e, 'Başlatılamadı')); } finally { setMesgul(''); }
  };

  const yenidenIsle = async () => {
    setMesgul('yeniden');
    try {
      const r = await api.post('/pos/aktarim/yeniden-isle', {});
      toast.success(r.data.adisyon ? `${r.data.adisyon} adisyon yeniden işleniyor` : r.data.mesaj);
      await yukle();
    } catch (e) { toast.error(hata(e, 'Yeniden işlenemedi')); } finally { setMesgul(''); }
  };

  if (!ayar) return <div className="text-center py-8 text-[#8a7e70]">Yükleniyor...</div>;
  const bekleyenSayisi = (ozet?.gunler || []).reduce((t, g) => t + (g.durumlar.failed || 0), 0);

  return (
    <div className="space-y-6" data-testid="pos-aktarim">
      <AyarKarti ayar={ayar} duzenler={duzenler} onKayit={setAyar} />
      <Eslestirmeler veri={eslesme} duzenler={duzenler} onDegisti={eslesmeYukle} />

      <Card className="bg-[#faf7f2] border-[#d0c4b0]">
        <CardHeader className="border-b border-[#d0c4b0]">
          <CardTitle className="text-lg font-bold text-[#1a1916] flex items-center gap-2">
            <Layers size={18} /> Aktarım
          </CardTitle>
        </CardHeader>
        <CardContent className="p-5 space-y-4">
          <ol className="text-xs text-[#6b5f4f] list-decimal ml-4 space-y-0.5">
            <li><b>Çek</b>: POS'taki adisyonlar, paket satışları ve cari tahsilatlar ERP'ye "hazır" olarak alınır — hiçbir şey işlenmez.</li>
            <li><b>Deneme</b>: yazılsa ne olacağı gösterilir — ciro, reçete, ödemeler, bekleyen eşleştirmeler.</li>
            <li><b>Uygula</b>: bir parti açılır; yazılan her kayıt partiye damgalanır. Tarih POS'un işletme günüdür.</li>
            <li><b>Geri al</b>: partinin tamamı, seçili günleri ya da seçili adisyonları geri alınır.</li>
          </ol>
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <label className="text-[11px] font-semibold text-[#6b5f4f] block mb-1">Başlangıç işletme günü</label>
              <Input type="date" value={aralik.bas} onChange={e => setAralik(a => ({ ...a, bas: e.target.value }))} className="w-40" />
            </div>
            <div>
              <label className="text-[11px] font-semibold text-[#6b5f4f] block mb-1">Bitiş işletme günü</label>
              <Input type="date" value={aralik.bit} onChange={e => setAralik(a => ({ ...a, bit: e.target.value }))} className="w-40" />
            </div>
            {duzenler && (
              <Button variant="outline" onClick={cek} disabled={!!mesgul} data-testid="aktarim-cek">
                {mesgul === 'cek' ? <Loader2 size={14} className="mr-2 animate-spin" /> : <Download size={14} className="mr-2" />}
                POS'tan Çek
              </Button>
            )}
            <Button onClick={deneme} disabled={!!mesgul} className="bg-[#2e2d29] text-white" data-testid="aktarim-deneme">
              {mesgul === 'deneme' ? <Loader2 size={14} className="mr-2 animate-spin" /> : <FlaskConical size={14} className="mr-2" />}
              Deneme Raporu
            </Button>
            {duzenler && bekleyenSayisi > 0 && (
              <Button variant="outline" disabled={!!mesgul} data-testid="bekleyenleri-isle"
                onClick={() => setOnay({ govde: { sadece_bekleyen: true }, baslik: `Bekleyen ${bekleyenSayisi.toLocaleString('tr-TR')} kayıt` })}>
                <Play size={14} className="mr-2" /> Bekleyenleri işle ({bekleyenSayisi.toLocaleString('tr-TR')})
              </Button>
            )}
            {silebilir && (
              <Button variant="outline" disabled={!!mesgul} onClick={yenidenIsle} data-testid="yeniden-isle"
                title="Reçetesi sonradan girilen ürünlerin satışlarını geri alıp yeniden yazar: stok ve maliyet tamamlanır">
                {mesgul === 'yeniden' ? <Loader2 size={14} className="mr-2 animate-spin" /> : <RefreshCw size={14} className="mr-2" />}
                Reçetesi tamamlananları yeniden işle
              </Button>
            )}
          </div>
          <StagingOzeti ozet={ozet} />
        </CardContent>
      </Card>

      {rapor && (
        <DenemeRaporu rapor={rapor} duzenler={duzenler} mesgul={mesgul}
          onUygula={() => setOnay({ govde: rapor.aralik, baslik: `${rapor.aralik.bas} – ${rapor.aralik.bit}`, rapor })}
          onKapat={() => setRapor(null)} onYenile={deneme} />
      )}

      <Partiler partiler={partiler} onDetay={setDetayId} />

      <Dialog open={!!onay} onOpenChange={o => !o && setOnay(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Aktarımı uygula</DialogTitle></DialogHeader>
          {onay && (
            <div className="space-y-3 text-sm">
              <p><b>{onay.baslik}</b> — işlenmemiş kayıtlar yeni bir partiye yazılacak.</p>
              <ul className="text-xs text-[#6b5f4f] list-disc ml-4 space-y-1">
                {onay.rapor && <li>Ciro: {trTutar(onay.rapor.ciro)} TRY, stok hareketi: {onay.rapor.stok_hareketi.toLocaleString('tr-TR')}</li>}
                {onay.rapor?.engelli_adisyon > 0 && (
                  <li className="text-red-700 font-semibold">
                    {onay.rapor.engelli_adisyon.toLocaleString('tr-TR')} kayıt eşleştirme bekliyor — YAZILMAYACAK.
                    Eşleştirince "Bekleyenleri işle" ile yazılır.
                  </li>
                )}
                <li>Kayıtlar POS'taki işletme gününe yazılır. Stok hareketi o günün stoğunu değiştirir; kesinleşmiş bir
                  sayımdan önceye düşen satış o sayımın farkına yansır.</li>
                <li>Parti arka planda çalışır; ilerlemesi aşağıdaki listede görünür.</li>
              </ul>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setOnay(null)}>Vazgeç</Button>
                <Button onClick={uygula} className="bg-[#3d6e4e] text-white" data-testid="aktarim-uygula-onay">
                  <Play size={14} className="mr-2" /> Uygula
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {detayId && (
        <PartiDetay id={detayId} silebilir={silebilir} onKapat={() => setDetayId(null)}
          onDegisti={yukle} />
      )}
    </div>
  );
}

/* ── Ayarlar: stok başlangıcı + gün sonu otomatiği ─────────────────────── */
function AyarKarti({ ayar, duzenler, onKayit }) {
  const a = ayar.ayarlar;
  const [kaydediliyor, setKaydediliyor] = useState(false);

  const kaydet = async (govde) => {
    setKaydediliyor(true);
    try {
      const r = await api.post('/pos/aktarim/ayarlar', govde);
      onKayit(r.data);
      toast.success('Aktarım ayarı kaydedildi');
    } catch (e) { toast.error(hata(e, 'Kaydedilemedi')); } finally { setKaydediliyor(false); }
  };

  return (
    <Card className="bg-[#faf7f2] border-[#d0c4b0]">
      <CardHeader className="border-b border-[#d0c4b0]">
        <CardTitle className="text-lg font-bold text-[#1a1916] flex items-center gap-2">
          <Settings size={18} /> Aktarım Ayarları
          {kaydediliyor && <Loader2 size={14} className="animate-spin" />}
        </CardTitle>
      </CardHeader>
      <CardContent className="p-5 grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="space-y-1.5">
          <label className="text-[11px] font-semibold text-[#6b5f4f] block">Stok düşümü başlangıç işletme günü</label>
          <Input type="date" defaultValue={a.stok_baslangic || ''} disabled={!duzenler} className="w-44"
            onBlur={e => e.target.value !== (a.stok_baslangic || '') && kaydet({ stok_baslangic: e.target.value || null })} />
          <p className="text-xs text-[#8a7e70]">
            Boş: bütün satışlar stoktan düşer. Bu günden önceki satışlar ciro ve maliyet olarak girer ama stoktan düşmez.
            Yalnızca bundan sonra uygulanan kayıtları etkiler.
          </p>
        </div>
        <div className="space-y-1.5">
          <div className="flex items-center gap-3">
            <Switch checked={!!a.otomatik} disabled={!duzenler || kaydediliyor}
              onCheckedChange={v => kaydet({ otomatik: v, otomatik_baslangic: a.otomatik_baslangic || bugun() })} />
            <span className="text-sm font-semibold text-[#1a1916]">Gün sonu otomatik aktarım</span>
          </div>
          <div className="flex items-center gap-2">
            <label className="text-[11px] text-[#6b5f4f]">Bu işletme gününden itibaren:</label>
            <Input type="date" defaultValue={a.otomatik_baslangic || ''} disabled={!duzenler} className="w-40 h-8"
              onBlur={e => e.target.value && e.target.value !== a.otomatik_baslangic && kaydet({ otomatik_baslangic: e.target.value })} />
          </div>
          <p className="text-xs text-[#8a7e70]">
            POS'ta gün kapanınca ERP birkaç dakika içinde o günü alır ve uygular. ERP kapalıysa açılınca kaçırdığı
            günleri sırayla alır. Eşleştirmesi eksik kayıt yazılmaz, bekler.
          </p>
          {(ayar.otomatik_gunler || []).length > 0 && (
            <div className="text-[11px] text-[#6b5f4f] space-y-0.5 pt-1">
              {ayar.otomatik_gunler.slice(0, 5).map(g => (
                <div key={g.tarih} className="flex items-center gap-1.5">
                  <Clock size={11} /> {g.tarih} · {g.durum === 'tamamlandi' ? `${g.kayit || 0} kayıt` : g.durum}
                  {g.hata && <span className="text-red-700">— {g.hata}</span>}
                </div>
              ))}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

/* ── Eşleştirmeler: POS'tan gelen her hesap bir kez bağlanır ─────────────── */
function Eslestirmeler({ veri, duzenler, onDegisti }) {
  const [acik, setAcik] = useState(false);
  const hesapSec = useMemo(() => (veri?.hesaplar || []).map(h => ({
    value: h.id, label: h.name, hint: h.currency || '',
    group: h.account_type === 'Kasa ve Bankalar' ? 'Kasa ve Bankalar' : (h.account_type || 'Diğer'),
  })).sort((a, b) => (a.group === 'Kasa ve Bankalar' ? 0 : 1) - (b.group === 'Kasa ve Bankalar' ? 0 : 1)
    || a.group.localeCompare(b.group, 'tr') || a.label.localeCompare(b.label, 'tr')), [veri]);
  const depoSec = useMemo(() => (veri?.depolar || []).map(d => ({ value: d.id, label: d.name })), [veri]);
  if (!veri) return null;
  const bolumler = [['odeme', veri.odeme, hesapSec], ['musteri', veri.musteri, hesapSec], ['depo', veri.depo, depoSec]];
  const bekleyen = bolumler.reduce((t, [, l]) => t + l.filter(x => !x.erp_id).length, 0);

  const esle = async (tur, satir, erpId) => {
    try {
      await api.post('/pos/aktarim/eslestirmeler', { tur, anahtar: satir.anahtar, erp_id: erpId || null, ad: satir.ad });
      toast.success(erpId ? `${satir.ad} eşleştirildi` : 'Eşleşme kaldırıldı');
      await onDegisti();
    } catch (e) { toast.error(hata(e, 'Eşleştirilemedi')); }
  };

  return (
    <Card className={`bg-[#faf7f2] ${bekleyen ? 'border-amber-400 border-2' : 'border-[#d0c4b0]'}`} data-testid="eslestirmeler">
      <CardHeader className="border-b border-[#d0c4b0] flex flex-row items-center justify-between gap-2">
        <CardTitle className="text-lg font-bold text-[#1a1916] flex items-center gap-2">
          <Link2 size={18} /> Eşleştirmeler
          {bekleyen > 0
            ? <Badge className="bg-amber-500 text-white">{bekleyen} eşleştirme bekliyor</Badge>
            : <Badge variant="outline" className="text-[#3d6e4e] border-[#3d6e4e]">Tamam</Badge>}
        </CardTitle>
        <Button variant="outline" size="sm" onClick={() => setAcik(x => !x)}>
          {acik ? 'Eşleşmişleri gizle' : `Eşleşmişleri göster (${bolumler.reduce((t, [, l]) => t + l.filter(x => x.erp_id).length, 0)})`}
        </Button>
      </CardHeader>
      <CardContent className="p-5 space-y-5">
        <p className="text-xs text-[#8a7e70]">
          POS'tan gelen her hesap ilk geldiğinde burada ERP karşılığına bir kez bağlanır, sonraki aktarımlar onu kullanır.
          Eşleşmesi olmayan kayıt yazılmaz. Kart bahşişi kart hesabına girer ve seçilen bahşiş hesabından çıkar
          (personele borç). Paket kredisi ve hediye çeki burada yoktur: paraları paket/çek satılırken ödeme yöntemiyle
          girer, adisyonda harcanınca ayrıca kasaya kayıt yazılmaz.
        </p>
        {bolumler.map(([tur, liste, secenekler]) => {
          const gorunen = acik ? liste : liste.filter(x => !x.erp_id);
          if (!gorunen.length) return null;
          return (
            <div key={tur}>
              <p className="text-[11px] font-semibold uppercase tracking-wide text-[#6b5f4f] mb-1">{ESLEME_TURU[tur]}</p>
              <div className="overflow-x-auto rounded border border-[#d0c4b0] bg-white">
                <table className="w-full text-xs">
                  <tbody>
                    {gorunen.map(x => (
                      <tr key={x.anahtar} className={`border-t border-[#e8dfd0] first:border-t-0 ${x.erp_id ? '' : 'bg-amber-50'}`}>
                        <td className="py-1.5 px-2">
                          <div className="font-medium">{x.ad}</div>
                          <div className="text-[10px] text-[#8a7e70]">{x.adet.toLocaleString('tr-TR')} kayıt{x.tutar ? ` · ${trTutar(x.tutar)} TRY` : ''}</div>
                        </td>
                        <td className="py-1.5 px-2 w-72">
                          <SearchableSelect value={x.erp_id || ''} options={secenekler} disabled={!duzenler}
                            onChange={v => esle(tur, x, v)} placeholder="Seçilmedi" />
                        </td>
                        <td className="py-1.5 px-2 w-16 text-right">
                          {x.erp_id && duzenler && (
                            <button type="button" className="text-[10px] text-[#8a7e70] underline" onClick={() => esle(tur, x, null)}>kaldır</button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          );
        })}
        {!bolumler.some(([, l]) => l.length) && <p className="text-sm text-[#8a7e70]">Önce POS'tan kayıt çekin.</p>}
      </CardContent>
    </Card>
  );
}

/* ── Staging: ay ay, açılınca gün gün ─────────────────────────────────── */
function StagingOzeti({ ozet }) {
  const [acikAy, setAcikAy] = useState(null);
  const aylar = useMemo(() => {
    const m = {};
    (ozet?.gunler || []).forEach(g => {
      const ay = (g.gun || '?').slice(0, 7);
      const a = m[ay] || (m[ay] = { ay, gunler: [], durumlar: {}, ciro: 0, degisen: 0 });
      a.gunler.push(g);
      a.ciro += g.ciro;
      a.degisen += g.degisen;
      Object.entries(g.durumlar).forEach(([k, v]) => { a.durumlar[k] = (a.durumlar[k] || 0) + v; });
    });
    return Object.values(m).sort((a, b) => b.ay.localeCompare(a.ay));
  }, [ozet]);
  if (!aylar.length) return <p className="text-sm text-[#8a7e70]">ERP'de henüz POS kaydı yok — önce "POS'tan Çek".</p>;
  const durumlar = ['hazir', 'completed', 'geri_alindi', 'failed', 'pending'];
  const satir = (anahtar, etiket, d, ciro, degisen, onClick, kalin) => (
    <tr key={anahtar} className={`border-t border-[#e8dfd0] ${onClick ? 'cursor-pointer hover:bg-[#f0e9dc]' : ''} ${kalin ? 'font-semibold' : ''}`} onClick={onClick}>
      <td className="py-1.5 px-2">{etiket}</td>
      {durumlar.map(k => <td key={k} className={`py-1.5 px-2 text-right ${k === 'failed' && d[k] ? 'text-red-700' : ''}`}>{d[k] ? d[k].toLocaleString('tr-TR') : '·'}</td>)}
      <td className="py-1.5 px-2 text-right">{trTutar(ciro)}</td>
      <td className="py-1.5 px-2 text-right">{degisen ? <span className="text-amber-700">{degisen}</span> : '·'}</td>
    </tr>
  );
  return (
    <div className="overflow-x-auto max-h-[420px] overflow-y-auto rounded border border-[#d0c4b0] bg-white">
      <table className="w-full text-xs">
        <thead className="bg-[#e8dfd0] sticky top-0">
          <tr>
            <th className="text-left py-1.5 px-2">İş günü</th>
            {durumlar.map(k => <th key={k} className="text-right py-1.5 px-2">{STAGING_DURUM[k]}</th>)}
            <th className="text-right py-1.5 px-2">Ciro (POS)</th>
            <th className="text-right py-1.5 px-2" title="İşlendikten sonra POS'ta değişen adisyon">Değişen</th>
          </tr>
        </thead>
        <tbody>
          {aylar.map(a => [
            satir(a.ay, `${acikAy === a.ay ? '▾' : '▸'} ${a.ay}`, a.durumlar, a.ciro, a.degisen,
              () => setAcikAy(x => x === a.ay ? null : a.ay), true),
            ...(acikAy === a.ay ? a.gunler.map(g => satir(g.gun, <span className="pl-4">{g.gun}</span>, g.durumlar, g.ciro, g.degisen)) : []),
          ])}
        </tbody>
      </table>
    </div>
  );
}

/* ── Deneme raporu ────────────────────────────────────────────────────── */
function DenemeRaporu({ rapor, duzenler, mesgul, onUygula, onKapat, onYenile }) {
  const { products } = useProducts();
  const [eslesmeler, setEslesmeler] = useState({});
  const [eksikSiniri, setEksikSiniri] = useState(50);

  // POS ürünü yalnızca MAMULE bağlanır; ürün olmayan satış iki özel seçenek (kullanıcı)
  const secenekler = useMemo(() => [
    { value: '__hizmet', label: 'Hizmet satışı (stok/maliyet yok)', group: 'Ürün değil' },
    { value: '__ucretli', label: 'Ücretli tüketim karşılığı (stok tüketimden)', group: 'Ürün değil' },
    ...products.filter(p => p.type === 'finished' && p.is_active !== false)
      .map(p => ({ value: p.id, label: p.name, hint: p.code, group: 'Mamul' }))
      .sort((a, b) => a.label.localeCompare(b.label, 'tr')),
  ], [products]);

  const esle = async (e, deger) => {
    const anahtar = e.ad;
    const govde = deger === '__hizmet' ? { satis_turu: 'hizmet' }
      : deger === '__ucretli' ? { satis_turu: 'ucretli_tuketim' }
      : deger ? { satis_turu: 'urun', product_card_id: deger } : {};
    try {
      await api.post('/pos/aktarim/urun-eslesmeleri', { anahtarlar: e.anahtarlar, pos_ad: e.ad, ...govde });
      setEslesmeler(x => ({ ...x, [anahtar]: deger }));
      toast.success(deger ? 'Eşleştirildi — raporu yenileyince yansır' : 'Eşleşme kaldırıldı');
    } catch (err) { toast.error(hata(err, 'Eşleştirilemedi')); }
  };

  const kutu = (etiket, deger, renk = '') => (
    <div className="rounded-lg border border-[#d0c4b0] bg-white px-3 py-2">
      <p className="text-[10px] uppercase tracking-wide text-[#8a7e70]">{etiket}</p>
      <p className={`text-lg font-bold ${renk}`}>{deger}</p>
    </div>
  );

  return (
    <Card className="bg-[#faf7f2] border-[#2e2d29] border-2" data-testid="aktarim-rapor">
      <CardHeader className="border-b border-[#d0c4b0] flex flex-row items-center justify-between gap-2 flex-wrap">
        <CardTitle className="text-lg font-bold text-[#1a1916] flex items-center gap-2">
          <FlaskConical size={18} /> Deneme Raporu · {rapor.aralik.bas} – {rapor.aralik.bit}
          <span className="text-xs font-normal text-[#8a7e70]">(hiçbir şey yazılmadı)</span>
        </CardTitle>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={onYenile} disabled={!!mesgul}>Yenile</Button>
          <Button variant="outline" size="sm" onClick={onKapat}>Kapat</Button>
          {duzenler && (
            <Button size="sm" onClick={onUygula} disabled={!!mesgul || (!rapor.adisyon && !rapor.tahsilat_kaydi && !rapor.paket_kaydi)}
              className="bg-[#3d6e4e] text-white" data-testid="aktarim-uygula">
              {mesgul === 'uygula' ? <Loader2 size={14} className="mr-2 animate-spin" /> : <Play size={14} className="mr-2" />} Uygula
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="p-5 space-y-5">
        <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-7 gap-2">
          {kutu('Adisyon / paket / tahsilat', `${rapor.adisyon.toLocaleString('tr-TR')} / ${rapor.paket_kaydi.toLocaleString('tr-TR')} / ${rapor.tahsilat_kaydi}`)}
          {kutu('Satış satırı', rapor.satir.toLocaleString('tr-TR'))}
          {kutu('Ciro (net)', `${trTutar(rapor.ciro)}`)}
          {kutu('İndirim', trTutar(rapor.brut - rapor.ciro))}
          {kutu('Maliyet (reçeteli)', trTutar(rapor.maliyet))}
          {kutu(`Stok hareketi${rapor.stok_baslangic ? ` (${rapor.stok_baslangic} →)` : ''}`, rapor.stok_hareketi.toLocaleString('tr-TR'))}
          {kutu('Bekleyecek kayıt', rapor.engelli_adisyon.toLocaleString('tr-TR'), rapor.engelli_adisyon ? 'text-red-700' : 'text-[#3d6e4e]')}
        </div>

        {rapor.eslestirme_bekleyen.length > 0 && (
          <div className="rounded-lg border border-amber-300 bg-amber-50 p-3">
            <p className="text-sm font-semibold text-amber-900 flex items-center gap-1.5 mb-1">
              <AlertTriangle size={14} /> Eşleştirme bekleyenler — bu kayıtlar uygulamada YAZILMAZ (yukarıdaki Eşleştirmeler kartından seçin)
            </p>
            <ul className="text-xs text-amber-900 space-y-0.5">
              {rapor.eslestirme_bekleyen.map(e => (
                <li key={`${e.esleme_turu}|${e.anahtar}`}>{ESLEME_TURU[e.esleme_turu]}: <b>{e.ad}</b> — {e.kayit.toLocaleString('tr-TR')} kayıt{e.tutar ? `, ${trTutar(e.tutar)} TRY` : ''}</li>
              ))}
            </ul>
          </div>
        )}
        {rapor.engeller.filter(e => e.tur !== 'eslesme').length > 0 && (
          <div className="rounded-lg border border-red-300 bg-red-50 p-3">
            <p className="text-sm font-semibold text-red-800 flex items-center gap-1.5 mb-1">
              <AlertTriangle size={14} /> Diğer engeller — bu kayıtlar uygulamada YAZILMAZ
            </p>
            <ul className="text-xs text-red-800 space-y-0.5">
              {rapor.engeller.filter(e => e.tur !== 'eslesme').map(e => <li key={e.mesaj}>{e.mesaj} — <b>{e.adet.toLocaleString('tr-TR')}</b> kayıt</li>)}
            </ul>
          </div>
        )}

        <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
          <Tablo baslik="Reçete durumu" kolonlar={['Durum', 'Satır', 'Adet', 'Ciro']}
            satirlar={Object.entries(rapor.recete).sort((a, b) => b[1].tutar - a[1].tutar).map(([k, d]) => [
              RECETE_DURUM[k] || k, d.satir.toLocaleString('tr-TR'), d.adet.toLocaleString('tr-TR'), trTutar(d.tutar)])} />
          <Tablo baslik="Ödemeler (TRY karşılığı; dövizde alınan döviz ayrıca)" kolonlar={['Ödeme', 'Durum', 'Hesap', 'Adet', 'Tutar']}
            satirlar={rapor.odemeler.map(o => [
              <span>{o.ad}{Object.keys(o.doviz || {}).length > 0 && (
                <span className="text-[#8a7e70]"> · {Object.entries(o.doviz).map(([pb, m]) => `${trPara(m)} ${pb}`).join(', ')}</span>)}</span>,
              <span className={o.durum === 'eslesmesiz' || o.durum === 'musterisiz' ? 'text-red-700 font-semibold' : ''}>{ODEME_DURUM[o.durum] || o.durum}</span>,
              o.hesap || '—', o.adet.toLocaleString('tr-TR'), trTutar(o.tutar)])} />
        </div>

        {rapor.eksik_receteler.length > 0 && (
          <div>
            <p className="text-sm font-semibold text-[#1a1916] mb-1">
              Reçetesi/kartı eksik POS ürünleri ({rapor.eksik_receteler.length}) — satış ve ciro yazılır, maliyet ve stok YAZILMAZ
            </p>
            <p className="text-[11px] text-[#8a7e70] mb-2">
              POS ürünü yalnızca bir MAMUL kartına bağlanır. Ürün olmayan satışlar için "Hizmet satışı" (iskele kirası,
              giriş ücreti) ya da "Ücretli tüketim karşılığı" (malzemesi ERP'de ücretli tüketim olarak girilen
              organizasyon, fix menü) seçin — ikisi de ciroya girer, stok düşmez. Alış fiyatlı ve adetle tutulan mamul
              (şişe bira, kutu kola) reçetesiz de kendi stoğundan düşer; diğer mamullerde reçete gerekir.
            </p>
            <div className="overflow-x-auto rounded border border-[#d0c4b0] bg-white">
              <table className="w-full text-xs">
                <thead className="bg-[#e8dfd0]">
                  <tr><th className="text-left py-1.5 px-2">POS ürünü</th><th className="text-left py-1.5 px-2">Ana kategori</th>
                    <th className="text-left py-1.5 px-2">Durum</th><th className="text-right py-1.5 px-2">Adet</th>
                    <th className="text-right py-1.5 px-2">Ciro</th><th className="text-left py-1.5 px-2 w-56">ERP kartı</th></tr>
                </thead>
                <tbody>
                  {rapor.eksik_receteler.slice(0, eksikSiniri).map(e => {
                    const anahtar = e.ad;
                    const secili = anahtar in eslesmeler ? eslesmeler[anahtar]
                      : (e.durumlar || []).includes('tur_uyumsuz') ? '' : e.kart?.id;
                    return (
                      <tr key={anahtar} className="border-t border-[#e8dfd0]">
                        <td className="py-1 px-2">{e.ad}{e.kod && <span className="text-[#8a7e70]"> · {e.kod}</span>}
                          {e.anahtarlar.length > 1 && <span className="text-[10px] text-[#8a7e70]"> · POS'ta {e.anahtarlar.length} kayıt</span>}</td>
                        <td className="py-1 px-2 text-[#6b5f4f] whitespace-nowrap">{e.kategori || '—'}</td>
                        <td className="py-1 px-2 whitespace-nowrap">{(e.durumlar || [e.durum]).map(d => RECETE_DURUM[d] || d).join(' / ')}</td>
                        <td className="py-1 px-2 text-right">{e.adet.toLocaleString('tr-TR')}</td>
                        <td className="py-1 px-2 text-right">{trTutar(e.tutar)}</td>
                        <td className="py-1 px-2">
                          {duzenler && e.anahtarlar.length ? (
                            <SearchableSelect value={secili || ''} options={secenekler} azamiSonuc={150}
                              onChange={v => esle(e, v)} placeholder="Mamul / hizmet / ücretli tüketim..." />
                          ) : (e.kart ? `${e.kart.kod} ${e.kart.ad}` : '—')}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {rapor.eksik_receteler.length > eksikSiniri && (
              <button type="button" className="text-xs underline text-[#6b5f4f] mt-1" onClick={() => setEksikSiniri(n => n + 100)}>
                {rapor.eksik_receteler.length - eksikSiniri} ürün daha göster
              </button>
            )}
          </div>
        )}

        {rapor.ornek_engelli.length > 0 && (
          <details className="text-xs">
            <summary className="cursor-pointer text-[#6b5f4f]">Engelli adisyon örnekleri</summary>
            <ul className="mt-1 space-y-0.5">
              {rapor.ornek_engelli.map(o => <li key={o.bilet}><b>{o.bilet}</b> ({o.gun}): {o.sorun.join('; ')}</li>)}
            </ul>
          </details>
        )}
      </CardContent>
    </Card>
  );
}

function Tablo({ baslik, kolonlar, satirlar }) {
  return (
    <div>
      <p className="text-sm font-semibold text-[#1a1916] mb-1">{baslik}</p>
      <div className="overflow-x-auto rounded border border-[#d0c4b0] bg-white">
        <table className="w-full text-xs">
          <thead className="bg-[#e8dfd0]">
            <tr>{kolonlar.map((k, i) => <th key={k} className={`py-1.5 px-2 ${i >= kolonlar.length - 2 && i > 0 ? 'text-right' : 'text-left'}`}>{k}</th>)}</tr>
          </thead>
          <tbody>
            {satirlar.map((s, i) => (
              <tr key={i} className="border-t border-[#e8dfd0]">
                {s.map((h, j) => <td key={j} className={`py-1 px-2 ${j >= s.length - 2 && j > 0 ? 'text-right' : ''}`}>{h}</td>)}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ── Partiler ─────────────────────────────────────────────────────────── */
function Partiler({ partiler, onDetay }) {
  return (
    <Card className="bg-[#faf7f2] border-[#d0c4b0]">
      <CardHeader className="border-b border-[#d0c4b0]">
        <CardTitle className="text-lg font-bold text-[#1a1916] flex items-center gap-2">
          <Layers size={18} /> Partiler
        </CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        {!partiler.length ? (
          <p className="p-5 text-sm text-[#8a7e70]">Henüz uygulanmış parti yok.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-[#e8dfd0]">
                <tr>
                  <th className="text-left py-2 px-3">Parti</th><th className="text-left py-2 px-3">Durum</th>
                  <th className="text-left py-2 px-3">Başlatan</th><th className="text-right py-2 px-3">İlerleme</th>
                  <th className="text-right py-2 px-3">Hatalı</th><th className="text-right py-2 px-3">Duran adisyon</th>
                  <th className="text-right py-2 px-3">Ciro</th><th className="py-2 px-3" />
                </tr>
              </thead>
              <tbody>
                {partiler.map(p => {
                  const il = p.ilerleme || {};
                  return (
                    <tr key={p.id} className="border-t border-[#e8dfd0]">
                      <td className="py-2 px-3">
                        <div className="font-semibold">{p.ad}</div>
                        <div className="text-[10px] text-[#8a7e70]">{(p.created_at || '').slice(0, 16).replace('T', ' ')}{p.gun_araligi?.[0] ? ` · ${p.gun_araligi[0]} → ${p.gun_araligi[1]}` : ''}</div>
                      </td>
                      <td className="py-2 px-3">
                        <Badge variant="outline" className={p.durum === 'calisiyor' ? 'border-blue-400 text-blue-700' : p.durum === 'hatali' ? 'border-red-400 text-red-700' : ''}>
                          {p.durum === 'calisiyor' && <Loader2 size={11} className="mr-1 animate-spin" />}
                          {PARTI_DURUM[p.durum] || p.durum}
                        </Badge>
                      </td>
                      <td className="py-2 px-3">{p.olusturan || '—'}</td>
                      <td className="py-2 px-3 text-right">{il.toplam ? `${(il.islenen || 0).toLocaleString('tr-TR')} / ${il.toplam.toLocaleString('tr-TR')}` : '—'}</td>
                      <td className={`py-2 px-3 text-right ${il.hatali ? 'text-red-700 font-semibold' : ''}`}>{il.hatali || '·'}</td>
                      <td className="py-2 px-3 text-right">{(p.kalan_adisyon || 0).toLocaleString('tr-TR')}</td>
                      <td className="py-2 px-3 text-right">{il.ciro != null ? trTutar(il.ciro) : '—'}</td>
                      <td className="py-2 px-3 text-right">
                        <Button size="sm" variant="outline" onClick={() => onDetay(p.id)}><Eye size={13} className="mr-1" /> Detay</Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function PartiDetay({ id, silebilir, onKapat, onDegisti }) {
  const [p, setP] = useState(null);
  const [secili, setSecili] = useState(new Set());
  const [adisyonlar, setAdisyonlar] = useState('');
  const [onay, setOnay] = useState(null);       // {kapsam, govde, metin}
  const [calisiyor, setCalisiyor] = useState(false);

  const yukle = useCallback(() => api.get(`/pos/aktarim/partiler/${id}`).then(r => setP(r.data))
    .catch(() => toast.error('Parti okunamadı')), [id]);
  useEffect(() => { yukle(); }, [yukle]);

  const geriAl = async () => {
    setCalisiyor(true);
    try {
      const r = await api.post(`/pos/aktarim/partiler/${id}/geri-al`, onay.govde);
      const s = r.data.sonuc || {};
      toast.success(`Geri alındı: ${r.data.adisyon} adisyon, ${s.pos_sales || 0} satış satırı, `
        + `${s.stok_hareketi || 0} stok hareketi, ${s.supplier_ledger || 0} cari kaydı`);
      setOnay(null); setSecili(new Set()); setAdisyonlar('');
      await yukle(); onDegisti();
    } catch (e) { toast.error(hata(e, 'Geri alınamadı')); } finally { setCalisiyor(false); }
  };

  const gunler = p?.gunler || [];
  const toplam = gunler.reduce((a, g) => ({ adisyon: a.adisyon + g.adisyon, ciro: a.ciro + g.ciro, maliyet: a.maliyet + g.maliyet,
    stok: a.stok + g.stok_hareketi, cari: a.cari + g.cari }), { adisyon: 0, ciro: 0, maliyet: 0, stok: 0, cari: 0 });
  const adisyonListesi = adisyonlar.split(/[\s,;]+/).map(x => x.trim()).filter(Boolean);

  return (
    <Dialog open onOpenChange={o => !o && onKapat()}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto [&>*]:min-w-0">
        <DialogHeader><DialogTitle>{p?.ad || 'Parti'}</DialogTitle></DialogHeader>
        {!p ? <Loader2 className="animate-spin mx-auto" /> : (
          <div className="space-y-4 text-sm">
            <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs text-[#6b5f4f]">
              <span>Durum: <b>{PARTI_DURUM[p.durum] || p.durum}</b></span>
              <span>Başlatan: {p.olusturan || '—'}</span>
              {p.ayar?.stok_baslangic && <span>Stok başlangıcı: {p.ayar.stok_baslangic}</span>}
              <span>Duran: <b>{toplam.adisyon.toLocaleString('tr-TR')}</b> adisyon · ciro {trTutar(toplam.ciro)} · maliyet {trTutar(toplam.maliyet)} · {toplam.stok.toLocaleString('tr-TR')} stok hareketi · cari {trTutar(toplam.cari)}</span>
            </div>

            {silebilir && toplam.adisyon > 0 && (
              <div className="rounded-lg border border-[#d0c4b0] bg-[#faf7f2] p-3 space-y-2">
                <p className="text-xs font-semibold text-[#1a1916] flex items-center gap-1"><RotateCcw size={13} /> Geri al</p>
                <div className="flex flex-wrap gap-2 items-center">
                  <Button size="sm" variant="outline" className="border-red-300 text-red-700"
                    onClick={() => setOnay({ govde: {}, metin: `Partinin TAMAMI (${toplam.adisyon.toLocaleString('tr-TR')} adisyon)` })}>
                    Tümünü geri al
                  </Button>
                  <Button size="sm" variant="outline" disabled={!secili.size}
                    onClick={() => setOnay({ govde: { gunler: [...secili] }, metin: `Seçili ${secili.size} gün` })}>
                    Seçili günleri geri al ({secili.size})
                  </Button>
                  <Input value={adisyonlar} onChange={e => setAdisyonlar(e.target.value)} placeholder="Adisyon no (virgül/boşlukla)"
                    className="h-8 w-64 text-xs" />
                  <Button size="sm" variant="outline" disabled={!adisyonListesi.length}
                    onClick={() => setOnay({ govde: { adisyonlar: adisyonListesi }, metin: `${adisyonListesi.length} adisyon` })}>
                    Adisyonları geri al
                  </Button>
                </div>
                <p className="text-[11px] text-[#8a7e70]">
                  Geri alınan adisyonun satış satırları, stok hareketleri (stok iade edilerek) ve cari/kasa kayıtları silinir;
                  adisyon "geri alındı" olur ve aynı aralık tekrar uygulanabilir.
                </p>
              </div>
            )}

            {gunler.length > 0 && (
              <div className="overflow-x-auto max-h-[45vh] overflow-y-auto rounded border border-[#d0c4b0]">
                <table className="w-full text-xs">
                  <thead className="bg-[#e8dfd0] sticky top-0">
                    <tr>
                      <th className="py-1.5 px-2 w-8">
                        {silebilir && <input type="checkbox" checked={secili.size === gunler.length}
                          onChange={e => setSecili(e.target.checked ? new Set(gunler.map(g => g.gun)) : new Set())} />}
                      </th>
                      <th className="text-left py-1.5 px-2">İş günü</th><th className="text-right py-1.5 px-2">Adisyon</th>
                      <th className="text-right py-1.5 px-2">Satır</th><th className="text-right py-1.5 px-2">Ciro</th>
                      <th className="text-right py-1.5 px-2">Maliyet</th><th className="text-right py-1.5 px-2">Stok hrk.</th>
                      <th className="text-right py-1.5 px-2">Kasa/cari</th>
                    </tr>
                  </thead>
                  <tbody>
                    {gunler.map(g => (
                      <tr key={g.gun} className="border-t border-[#e8dfd0]">
                        <td className="py-1 px-2 text-center">
                          {silebilir && <input type="checkbox" checked={secili.has(g.gun)} onChange={e => setSecili(s => {
                            const n = new Set(s); if (e.target.checked) n.add(g.gun); else n.delete(g.gun); return n;
                          })} />}
                        </td>
                        <td className="py-1 px-2">{g.gun}</td>
                        <td className="py-1 px-2 text-right">{g.adisyon}</td>
                        <td className="py-1 px-2 text-right">{g.satir}</td>
                        <td className="py-1 px-2 text-right">{trPara(g.ciro)}</td>
                        <td className="py-1 px-2 text-right">{trPara(g.maliyet)}</td>
                        <td className="py-1 px-2 text-right">{g.stok_hareketi || '·'}</td>
                        <td className="py-1 px-2 text-right">{trPara(g.cari)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {(p.hatalar || []).length > 0 && (
              <details open className="text-xs">
                <summary className="cursor-pointer font-semibold text-red-700">Yazılmayan adisyonlar ({p.hatalar.length})</summary>
                <ul className="mt-1 space-y-0.5 max-h-48 overflow-y-auto">
                  {p.hatalar.map((h, i) => <li key={i}><b>{h.bilet}</b> ({h.gun}): {h.hata}</li>)}
                </ul>
              </details>
            )}

            {(p.geri_almalar || []).length > 0 && (
              <details className="text-xs">
                <summary className="cursor-pointer text-[#6b5f4f]">Geri alma geçmişi ({p.geri_almalar.length})</summary>
                <ul className="mt-1 space-y-0.5">
                  {p.geri_almalar.map((g, i) => (
                    <li key={i}>{(g.zaman || '').slice(0, 16).replace('T', ' ')} · {g.kullanici} · {g.kapsam}
                      {g.gunler ? ` (${g.gunler.join(', ')})` : ''}{g.adisyonlar ? ` (${g.adisyonlar.join(', ')})` : ''} · {g.adisyon} adisyon</li>
                  ))}
                </ul>
              </details>
            )}
          </div>
        )}

        <Dialog open={!!onay} onOpenChange={o => !o && setOnay(null)}>
          <DialogContent className="max-w-sm">
            <DialogHeader><DialogTitle>Geri alınsın mı?</DialogTitle></DialogHeader>
            <p className="text-sm"><b>{onay?.metin}</b> geri alınacak: satışlar, stok hareketleri ve cari/kasa kayıtları silinir, stok iade edilir.</p>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setOnay(null)}>Vazgeç</Button>
              <Button onClick={geriAl} disabled={calisiyor} className="bg-red-700 text-white" data-testid="geri-al-onay">
                {calisiyor ? <Loader2 size={14} className="mr-2 animate-spin" /> : <CheckCircle size={14} className="mr-2" />} Geri al
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </DialogContent>
    </Dialog>
  );
}
